import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  PropsWithChildren,
} from 'react';
import * as firebase from 'firebase/app';
import 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { AppPath } from 'AppRoutes';

const FIREBASE_CONFIG = {
  apiKey: process.env.REACT_APP_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_DATABASE_URL,
  projectId: process.env.REACT_APP_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_APP_ID,
};

export type GetUserToken = () => Promise<string | null>;
type Auth = {
  status: 'loading' | 'loggedIn' | 'loggedOut';
  login: (
    email: string,
    password: string
  ) => Promise<firebase.auth.UserCredential>;
  logout: () => Promise<void>;
  getUserId: () => string | null;
  getUserToken: GetUserToken;
};
const AuthContext = createContext<Auth | null>(null);

const firebaseAuth = !firebase.apps.length
  ? firebase.initializeApp(FIREBASE_CONFIG).auth()
  : firebase.app().auth();

const login = async (email: string, password: string) =>
  firebaseAuth.signInWithEmailAndPassword(email, password).catch((error) => {
    throw formatError(error).message;
  });

const logout = () => firebaseAuth.signOut();

const getUserToken = async () => firebaseAuth.currentUser?.getIdToken() ?? null;
console.log(firebaseAuth.currentUser?.uid);
const getUserId = () => firebaseAuth.currentUser?.uid ?? null;

export const AuthProvider = ({ children }: PropsWithChildren<{}>) => {
  const [auth, setAuth] = useState<Auth>({
    status: 'loading',
    login,
    logout,
    getUserId,
    getUserToken,
  });

  const navigate = useNavigate();

  useEffect(() => {
    const unSub = firebaseAuth.onAuthStateChanged((user) => {
      setAuth((p) => ({ ...p, status: user ? 'loggedIn' : 'loggedOut' }));
      if (!user) navigate(AppPath.Login);
      if (user && window.location.pathname === AppPath.Login)
        navigate(AppPath.Home);
    });

    return () => unSub();
  }, [navigate]);

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};

export const useAuth = (): Auth => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within a AuthProvider');

  return context;
};

const formatError = (error: firebase.FirebaseError): firebase.FirebaseError => {
  switch (error.code) {
    case 'auth/invalid-email':
      error.message = '無効なログイン名';
      break;

    case 'auth/wrong-password':
      error.message = 'ログイン名かパスワードが間違っています';
      break;

    case 'auth/user-not-found':
      error.message = 'ログイン名かパスワードが間違っています';
      break;

    case 'auth/too-many-requests':
      error.message = 'しばらく待ってから送信してください';
      break;

    default:
      break;
  }
  return error;
};
